import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Owner } from '../../../@backend';

const TRANSITION_STORAGE_KEY = 'canvasTenantTransition';
const TRANSITION_MAX_AGE_MS = 15_000;
const LEAVE_DURATION_MS = 1_150;
const REVEAL_DURATION_MS = 850;
const REVEAL_FALLBACK_MS = 4_000;

const OWNER_LOGO_URLS: Readonly<Record<number, string>> = {
  1: 'assets/img/logos/kodl-logo.svg',
  2: 'assets/img/logos/artslimit-logo.svg',
  3: 'assets/img/logos/contemporary.png',
};

export interface TenantBrand {
  id: number;
  name: string;
  shortcut: string;
  logoUrl?: string;
}

export interface TenantTransitionState {
  from: TenantBrand;
  to: TenantBrand;
  phase: 'leaving' | 'holding' | 'revealing';
}

interface StoredTenantTransition {
  from: TenantBrand;
  to: TenantBrand;
  createdAt: number;
}

@Injectable({ providedIn: 'root' })
export class TenantTransitionService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<TenantTransitionState | null>(null);
  private reloadTimer?: ReturnType<typeof setTimeout>;
  private revealTimer?: ReturnType<typeof setTimeout>;
  private previousBodyOverflow?: string;

  readonly state$ = this.stateSubject.asObservable();

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.restorePendingTransition();
  }

  start(fromOwner: Owner, toOwner: Owner): void {
    if(this.stateSubject.value) return;

    const transition: StoredTenantTransition = {
      from: this.toBrand(fromOwner),
      to: this.toBrand(toOwner),
      createdAt: Date.now(),
    };

    sessionStorage.setItem(TRANSITION_STORAGE_KEY, JSON.stringify(transition));
    this.lockPage();
    this.stateSubject.next({ ...transition, phase: 'leaving' });

    this.reloadTimer = setTimeout(
      () => window.location.reload(),
      this.motionDuration(LEAVE_DURATION_MS),
    );
  }

  revealIfPending(): void {
    const state = this.stateSubject.value;
    if(state?.phase !== 'holding') return;

    if(this.revealTimer) clearTimeout(this.revealTimer);
    sessionStorage.removeItem(TRANSITION_STORAGE_KEY);
    this.stateSubject.next({ ...state, phase: 'revealing' });

    this.revealTimer = setTimeout(() => {
      this.stateSubject.next(null);
      this.unlockPage();
    }, this.motionDuration(REVEAL_DURATION_MS));
  }

  ngOnDestroy(): void {
    if(this.reloadTimer) clearTimeout(this.reloadTimer);
    if(this.revealTimer) clearTimeout(this.revealTimer);
    this.unlockPage();
  }

  private restorePendingTransition(): void {
    const rawTransition = sessionStorage.getItem(TRANSITION_STORAGE_KEY);
    if(!rawTransition) return;

    try {
      const transition = JSON.parse(rawTransition) as StoredTenantTransition;
      const isFresh = Date.now() - transition.createdAt < TRANSITION_MAX_AGE_MS;
      if(!isFresh || !transition.from || !transition.to) {
        sessionStorage.removeItem(TRANSITION_STORAGE_KEY);
        return;
      }

      this.lockPage();
      this.stateSubject.next({ ...transition, phase: 'holding' });
      this.revealTimer = setTimeout(() => this.revealIfPending(), REVEAL_FALLBACK_MS);
    } catch {
      sessionStorage.removeItem(TRANSITION_STORAGE_KEY);
    }
  }

  private toBrand(owner: Owner): TenantBrand {
    const id = owner.id ?? 0;
    return {
      id,
      name: owner.name,
      shortcut: owner.shortcut,
      logoUrl: OWNER_LOGO_URLS[id],
    };
  }

  private lockPage(): void {
    this.previousBodyOverflow = this.document.body.style.overflow;
    this.document.body.classList.add('tenant-transition-active');
    this.document.body.style.overflow = 'hidden';
  }

  private unlockPage(): void {
    this.document.body.classList.remove('tenant-transition-active');
    this.document.body.style.overflow = this.previousBodyOverflow ?? '';
    this.previousBodyOverflow = undefined;
  }

  private motionDuration(duration: number): number {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 50 : duration;
  }
}
